import type { ApolloCache } from '@apollo/client';
import { useMutation } from '@apollo/client';
import * as _ from 'lodash-es';
import { useCallback } from 'react';
import type { SetOptional } from 'type-fest';
import topicPolicyCheck, {
  TopicPolicyAction,
  UserRole,
} from './topicPolicyCheck';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import ConfirmationModal from '../components/ConfirmationModal';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type {
  TopicGroupRole,
  TopicUserRole,
  User,
} from '../graphql/generated/graphql';
import { UserType } from '../graphql/generated/graphql';
import { UIActions } from '../ui/uiSlice';

// eslint-disable-next-line slab-graphql/no-unused-fields
gql(`
  fragment useTopicUserMutations_user on User {
    id
    name
    type
    avatar {
      thumb
      original
    }
  }
`);

const ADD_USER_MUTATION = gql(/** @complexity 4 */ `
  mutation addUserToTopic($topicId: ID!, $userId: ID!) {
    user: addUserToTopic(topicId: $topicId, userId: $userId) {
      ...useTopicUserMutations_user
    }
  }
`);

const REMOVE_USER_MUTATION = gql(/** @complexity 4 */ `
  mutation removeUserFromTopic($topicId: ID!, $userId: ID!) {
    user: removeUserFromTopic(topicId: $topicId, userId: $userId) {
      ...useTopicUserMutations_user
    }
  }
`);

const UPDATE_USER_MUTATION = gql(/** @complexity 4 */ `
  mutation updateUserInTopic(
    $oldRole: TopicUserRole!
    $topicId: ID!
    $userId: ID!
    $role: TopicUserRole!
  ) {
    user: updateUserInTopic(
      oldRole: $oldRole
      topicId: $topicId
      userId: $userId
      role: $role
    ) {
      ...useTopicUserMutations_user
    }
  }
`);

const UPDATE_INHERIT_MUTATION = gql(/** @complexity 2 */ `
  mutation updateInheritParent($topicId: ID!, $inheritParent: Boolean!) {
    updateTopic(id: $topicId, inheritParent: $inheritParent) {
      id
    }
  }
`);

const REMOVE_GROUP_MUTATION = gql(/** @complexity 2 */ `
  mutation removeGroupFromTopic($topicId: ID!, $groupId: ID!) {
    group: removeGroupFromTopic(topicId: $topicId, groupId: $groupId) {
      id
    }
  }
`);

const UPDATE_GROUP_MUTATION = gql(/** @complexity 2 */ `
  mutation updateGroupInTopic(
    $oldRole: TopicGroupRole!
    $topicId: ID!
    $groupId: ID!
    $role: TopicGroupRole!
  ) {
    group: updateGroupInTopic(
      oldRole: $oldRole
      topicId: $topicId
      groupId: $groupId
      role: $role
    ) {
      id
    }
  }
`);

const ADD_GROUP_MUTATION = gql(/** @complexity 2 */ `
  mutation addGroupToTopic($topicId: ID!, $groupId: ID!) {
    group: addGroupToTopic(topicId: $topicId, groupId: $groupId) {
      id
    }
  }
`);

const REQUEST_TOPIC_ACCESS_MUTATION = gql(/** @complexity 4 */ `
  mutation requestTopicAccess($id: ID!) {
    user: requestTopicAccess(id: $id) {
      ...useTopicUserMutations_user
    }
  }
`);

const MEMBERSHIP_TYPE_TO_ID_KEY = {
  member: 'memberUserIds',
  owner: 'ownerUserIds',
  requestor: 'requestorUserIds',
  memberGroup: 'memberGroupIds',
  ownerGroup: 'ownerGroupIds',
} as const;

type MembershipType = keyof typeof MEMBERSHIP_TYPE_TO_ID_KEY;

const updateCache = <Cache>(
  cache: ApolloCache<Cache>,
  topicId: string,
  user?: { id: string },
  add = true,
  membershipType: MembershipType = 'member',
) => {
  if (!user) return;

  const idListKey = MEMBERSHIP_TYPE_TO_ID_KEY[membershipType];

  if (!idListKey) {
    return;
  }

  cache.modify({
    id: cache.identify({ __typename: 'Topic', id: topicId }),
    fields: {
      membership(existingMembership) {
        if (add) {
          return {
            ...existingMembership,
            [idListKey]: _.uniq([...existingMembership[idListKey], user.id]),
          };
        }

        return {
          ...existingMembership,
          [idListKey]: existingMembership[idListKey].filter(
            (id: string) => id !== user.id,
          ),
        };
      },
    },
  });
};

const USE_REMOVE_TOPIC_USER_RESULT_TOPIC_FRAGMENT = gql(`
  fragment useRemoveTopicUserResult_topic on Topic {
    id
    privacy
    owners {
      id
    }
    members {
      id
    }
    ...topicPolicyCheck_topic
  }
`);

export const useRemoveTopicUser = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);
  const [mutate] = useMutation(REMOVE_USER_MUTATION);

  return useCallback(
    (
      topic:
        | { id: string; owners?: never; members?: never; privacy?: never }
        | DocumentType<typeof USE_REMOVE_TOPIC_USER_RESULT_TOPIC_FRAGMENT>,
      user:
        | { id: string; name?: never }
        | (Extract<Parameters<typeof topicPolicyCheck>[2], { id: string }> &
            DocumentType<typeof REMOVE_USER_MUTATION>['user']),
    ) => {
      let membershipType: MembershipType = 'requestor';
      // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
      let showWarning = false;

      if (_.some(topic.owners, { id: user.id })) {
        membershipType = 'owner';
      } else if (_.some(topic.members, { id: user.id })) {
        membershipType = 'member';
        showWarning =
          user.id === currentUser.id &&
          !!topic.privacy &&
          !topicPolicyCheck(
            TopicPolicyAction.AddRemoveMember,
            topic,
            currentUser.type === UserType.Guest
              ? UserRole.Guest
              : UserRole.Full,
          );
      }

      const doRemove = () =>
        mutate({
          variables: { topicId: topic.id, userId: user.id },
          optimisticResponse: user.name
            ? {
                user: {
                  ...user,
                  __typename: 'User',
                },
              }
            : undefined,
          update: (cache, result) => {
            updateCache(
              cache,
              topic.id,
              result.data?.user,
              false,
              membershipType,
            );

            cache.modify<User>({
              id: cache.identify(user),
              fields: {
                membershipTopics(existingRefs, { readField }) {
                  return existingRefs.filter(
                    (ref) => readField('id', ref) !== topic.id,
                  );
                },
              },
            });
          },
        });

      return showWarning
        ? new Promise<Awaited<ReturnType<typeof doRemove>>>(
            (resolve, reject) => {
              dispatch(
                UIActions.openModal(ConfirmationModal, {
                  title: 'Leave topic?',
                  message:
                    'You will need to request access if you would like to rejoin this topic.',
                  onConfirm: () => doRemove().then(resolve).catch(reject),
                }),
              );
            },
          )
        : doRemove();
    },
    [currentUser, dispatch, mutate],
  );
};

const USE_UPDATE_TOPIC_USER_TOPIC_FRAGMENT = gql(`
  fragment useUpdateTopicUser_topic on Topic {
    id
  }
`);

export const useUpdateTopicUser = () => {
  const [mutate] = useMutation(UPDATE_USER_MUTATION);

  return useCallback(
    (
      {
        id: topicId,
      }: DocumentType<typeof USE_UPDATE_TOPIC_USER_TOPIC_FRAGMENT>,
      user: SetOptional<
        DocumentType<typeof UPDATE_USER_MUTATION>['user'],
        'name' | 'type'
      >,
      oldRole: MembershipType,
      role: MembershipType,
    ) =>
      mutate({
        variables: {
          topicId,
          userId: user.id,
          oldRole: oldRole.toUpperCase() as TopicUserRole,
          role: role.toUpperCase() as TopicUserRole,
        },
        optimisticResponse: {
          user: {
            ...user,
            type: user.type ?? UserType.User,
            name: user.name ?? '',
            __typename: 'User',
          },
        },
        update: (proxy, result) => {
          const resultUser = result.data?.user;
          updateCache(proxy, topicId, resultUser, true, role);
          updateCache(proxy, topicId, resultUser, false, oldRole);
        },
      }),
    [mutate],
  );
};

const USE_ADD_TOPIC_USER_TOPIC_FRAGMENT = gql(`
  fragment useAddTopicUser_topic on Topic {
    id
    members {
      id
    }
    owners {
      id
    }
    requestors {
      id
    }
  }
`);

export const useAddTopicUser = () => {
  const [mutate] = useMutation(ADD_USER_MUTATION);
  const updateTopicUser = useUpdateTopicUser();

  return useCallback(
    async (
      topic: DocumentType<typeof USE_ADD_TOPIC_USER_TOPIC_FRAGMENT>,
      user: SetOptional<DocumentType<typeof ADD_USER_MUTATION>['user'], 'name'>,
    ) => {
      const { id: topicId, members, owners, requestors } = topic;
      const { id: userId, type } = user;
      const isAdmin = type === UserType.Admin;

      if (_.some(owners, { id: userId }) || _.some(members, { id: userId })) {
        return Promise.resolve();
      }

      if (_.some(requestors, { id: userId })) {
        return updateTopicUser(topic, user, 'requestor', 'member');
      }

      const res = await mutate({
        variables: { topicId, userId },
        optimisticResponse: {
          user: {
            ...user,
            name: user.name ?? '',
            __typename: 'User',
          },
        },
        update: (cache, result) => {
          updateCache(
            cache,
            topicId,
            result.data?.user,
            true,
            isAdmin ? 'owner' : 'member',
          );
          cache.modify<User>({
            id: cache.identify(user),
            fields: {
              membershipTopics(existingRefs, { readField }) {
                if (
                  existingRefs.some((ref) => readField('id', ref) === topic.id)
                ) {
                  return existingRefs;
                }
                return [
                  ...existingRefs,
                  cache.writeFragment({
                    data: topic,
                    fragment: gql(`
                  fragment useTopicUserMutation_membershipTopics on Topic {
                    id
                  }
                `),
                  }),
                ];
              },
            },
          });
        },
      });
      if (isAdmin) {
        await updateTopicUser(topic, user, 'member', 'owner');
      }
      return res;
    },
    [mutate, updateTopicUser],
  );
};

const USE_ADD_TOPIC_GROUP_TOPIC_FRAGMENT = gql(`
  fragment useAddTopicGroup_topic on Topic {
    id
    ownerGroups {
      id
    }
    memberGroups {
      id
    }
  }
`);

export const useAddTopicGroup = () => {
  const [mutate] = useMutation(ADD_GROUP_MUTATION);

  return useCallback(
    (
      topic: DocumentType<typeof USE_ADD_TOPIC_GROUP_TOPIC_FRAGMENT>,
      group: DocumentType<typeof ADD_GROUP_MUTATION>['group'],
    ) => {
      const { id: topicId, memberGroups, ownerGroups } = topic;
      const { id: groupId } = group;

      if (
        _.some(ownerGroups, { id: group.id }) ||
        _.some(memberGroups, { id: group.id })
      ) {
        return Promise.resolve();
      }

      return mutate({
        variables: { topicId, groupId },
        optimisticResponse: {
          group: {
            ...group,
            __typename: 'Group',
          },
        },
        update: (proxy, result) => {
          updateCache(proxy, topicId, result.data?.group, true, 'memberGroup');
        },
      });
    },
    [mutate],
  );
};

const USE_REMOVE_TOPIC_GROUP_TOPIC_FRAGMENT = gql(`
  fragment useRemoveTopicGroup_topic on Topic {
    id
    ownerGroups {
      id
    }
    memberGroups {
      id
    }
  }
`);

export const useRemoveTopicGroup = () => {
  const [mutate] = useMutation(REMOVE_GROUP_MUTATION);

  return useCallback(
    (
      topic: DocumentType<typeof USE_REMOVE_TOPIC_GROUP_TOPIC_FRAGMENT>,
      group: DocumentType<typeof REMOVE_GROUP_MUTATION>['group'],
    ) => {
      let type: MembershipType | undefined;

      if (_.some(topic.ownerGroups, { id: group.id })) {
        type = 'ownerGroup';
      } else if (_.some(topic.memberGroups, { id: group.id })) {
        type = 'memberGroup';
      }

      return type
        ? mutate({
            variables: { topicId: topic.id, groupId: group.id },
            optimisticResponse: {
              group: {
                ...group,
                __typename: 'Group',
              },
            },
            update: (proxy, result) => {
              updateCache(proxy, topic.id, result.data?.group, false, type);
            },
          })
        : null;
    },
    [mutate],
  );
};

const USE_UPDATE_TOPIC_GROUP_TOPIC_FRAGMENT = gql(`
  fragment useUpdateTopicGroup_topic on Topic {
    id
  }
`);

export const useUpdateTopicGroup = () => {
  const [mutate] = useMutation(UPDATE_GROUP_MUTATION);

  return useCallback(
    (
      {
        id: topicId,
      }: DocumentType<typeof USE_UPDATE_TOPIC_GROUP_TOPIC_FRAGMENT>,
      group: { id: string },
      oldRole: 'member' | 'owner',
      role: 'member' | 'owner',
    ) =>
      mutate({
        variables: {
          topicId,
          groupId: group.id,
          oldRole: oldRole.toUpperCase() as TopicGroupRole,
          role: role.toUpperCase() as TopicGroupRole,
        },
        optimisticResponse: {
          group: {
            ...group,
            // @ts-expect-error -- TODO: Result type from updateGroupInTopic is User instead of Group
            __typename: 'Group',
          },
        },
        update: (proxy, result) => {
          const resultGroup = result.data?.group;
          updateCache(proxy, topicId, resultGroup, true, `${role}Group`);
          updateCache(proxy, topicId, resultGroup, false, `${oldRole}Group`);
        },
      }),
    [mutate],
  );
};

const USE_UPDATE_INHERIT_PARENT_TOPIC_FRAGMENT = gql(`
  fragment useUpdateInheritParent_topic on Topic {
    id
  }
`);

export const useUpdateInheritParent = () => {
  const [mutate] = useMutation(UPDATE_INHERIT_MUTATION);

  return useCallback(
    (
      {
        id: topicId,
      }: DocumentType<typeof USE_UPDATE_INHERIT_PARENT_TOPIC_FRAGMENT>,
      inheritParent: boolean,
    ) =>
      mutate({
        variables: { topicId, inheritParent },
      }),
    [mutate],
  );
};

export const useRequestAccess = () => {
  const [mutate] = useMutation(REQUEST_TOPIC_ACCESS_MUTATION);
  const user = useAppSelector((state) => state.session.user);

  return useCallback(
    (topicId: string) =>
      mutate({
        variables: { id: topicId },
        optimisticResponse: {
          user: {
            ...user,
            __typename: 'User',
          },
        },
        update: (proxy, result) => {
          updateCache(proxy, topicId, result.data?.user, true, 'requestor');
        },
      }),
    [mutate, user],
  );
};
